/**
 * Drapeaux de fonctionnalités (§16, règle commune).
 *
 * Chaque feature signature doit pouvoir être coupée en production sans redéployer la
 * logique du jeu. Ces drapeaux sont lus au démarrage ; en V1 ils pourront être surchargés
 * par une réponse serveur (table de configuration Supabase) sans changer les appels.
 *
 * Convention : une feature non encore implémentée reste à `false` — le drapeau existe
 * pour que le code appelant soit écrit dès maintenant du bon côté de la frontière.
 */
export interface FeatureFlags {
  /**
   * §9 — boucle virale complète : sync, page publique, visites, boosts, partage.
   * Tant qu'il est coupé, les quêtes « reçois des visiteurs » sortent de la rotation :
   * un objectif impossible à réaliser est exactement ce que le §1 interdit.
   */
  readonly viralLoop: boolean;
  /** §16.1 — décorations laissées par les visiteurs (MVP). */
  readonly visitorDecorations: boolean;
  /** §16.2 — météo réelle via Open-Meteo (V1). */
  readonly realWeather: boolean;
  /** §16.3 — jumelage de villes (V2). */
  readonly cityTwinning: boolean;
  /**
   * §16.4 — monuments d'héritage au prestige (V1).
   *
   * ⚠️ **BLOQUÉ SUR UN ASSET, mesuré le 22 août.** Tout le reste est prêt : le type
   * `Monument` existe, `save.ts` le relit champ par champ, et la remise à zéro d'ère les
   * fait déjà traverser les ères en écartant ceux qui tombent hors de la grille neuve.
   * Il ne manque que la CRÉATION — et un objet à montrer.
   *
   * Le pack POLYGON MINI n'a **aucun monument** : ni statue, ni obélisque, ni mémorial.
   * C'est un pack de ville. Deux candidats ont été rendus puis écartés sur mesure, pas
   * sur intuition :
   *
   *  · `SM_Prop_FlagPole_01` — le nom promet un mât, l'objet est un **drapeau américain**
   *    incliné comme une applique murale. Ni neutre pour un jeu français, ni monumental
   *    (règle 14 : le nom ne décrit pas la forme) ;
   *  · `SM_Tile_Cube_Column_01` — la **strate rocheuse sous l'île**, terre au-dessus et
   *    roche en dessous, faite pour pendre sous le plateau flottant. Un rocher posé
   *    debout ne se lit pas comme un mémorial.
   *
   * Poser l'un des deux aurait donné une feature « livrée » que personne n'aurait
   * comprise à l'écran. Le drapeau reste donc baissé jusqu'à ce qu'il y ait un objet à
   * montrer — asset acheté, ou idée visuelle qui se compose de ce qu'on a.
   */
  readonly legacyMonuments: boolean;
  /** §16.5 — capsule temporelle à 365 jours (V1). */
  readonly timeCapsule: boolean;
  /** §16.6 — livre d'or emoji (V1). */
  readonly emojiGuestbook: boolean;
  /** §15 — ligues hebdomadaires (MVP). */
  readonly weeklyLeagues: boolean;
  /** §9 — présence des visiteurs en temps réel (V1). */
  readonly realtimeVisitors: boolean;
  /** §14 — rewarded video : emplacements visibles au MVP, activation V1. */
  readonly rewardedAds: boolean;
  /**
   * ROADMAP 6.3 — case de consentement newsletter.
   *
   * ⚠️ **Ce drapeau ne protège pas une feature incertaine : il attend une MIGRATION.**
   * La table `newsletter_consents` (`20260820190000`) n'est pas encore appliquée au
   * projet distant. Sans elle, l'insertion échoue et la case afficherait « impossible
   * d'enregistrer ton choix » à chaque tap — un défaut visible pour une feature qui,
   * elle, est finie.
   *
   * À passer à `true` **le jour où la migration est appliquée**, et pas avant. La
   * commande est dans `A-FAIRE-BENOIT.md`.
   */
  readonly newsletterConsent: boolean;
}

export const FEATURES: FeatureFlags = {
  viralLoop: true,
  visitorDecorations: true,
  realWeather: false,
  cityTwinning: false,
  legacyMonuments: false,
  timeCapsule: false,
  emojiGuestbook: false,
  weeklyLeagues: false,
  realtimeVisitors: true,
  rewardedAds: false,
  // Migration `20260820190000_newsletter_consents.sql` appliquée au distant le 21 août.
  newsletterConsent: true,
};
